import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Order {
  id: string;
  orderNumber: string;
  customer: string;
  items: number;
  total: number;
  paymentMethod: string;
  channel: "pos" | "online";
  status: "completed" | "pending" | "refunded" | "cancelled";
  date: string;
  time: string;
  cashier?: string;
}

const MOCK_ORDERS: Order[] = [
  { id: "1", orderNumber: "ORD-2024-001", customer: "John Doe", items: 3, total: 125.99, paymentMethod: "Card", channel: "pos", status: "completed", date: "2024-01-15", time: "10:30 AM", cashier: "Maria G." },
  { id: "2", orderNumber: "ORD-2024-002", customer: "Jane Smith", items: 5, total: 289.50, paymentMethod: "Cash", channel: "pos", status: "completed", date: "2024-01-15", time: "11:15 AM", cashier: "Carlos R." },
  { id: "3", orderNumber: "ORD-2024-003", customer: "Guest", items: 2, total: 45.00, paymentMethod: "Card", channel: "online", status: "pending", date: "2024-01-15", time: "12:00 PM" },
  { id: "4", orderNumber: "ORD-2024-004", customer: "Mike Johnson", items: 1, total: 89.99, paymentMethod: "Card", channel: "online", status: "completed", date: "2024-01-15", time: "02:30 PM" },
  { id: "5", orderNumber: "ORD-2024-005", customer: "Sarah Wilson", items: 4, total: 156.75, paymentMethod: "Split", channel: "pos", status: "refunded", date: "2024-01-14", time: "04:45 PM", cashier: "Maria G." },
  { id: "6", orderNumber: "ORD-2024-006", customer: "Guest", items: 2, total: 67.00, paymentMethod: "Card", channel: "pos", status: "cancelled", date: "2024-01-14", time: "05:20 PM", cashier: "Carlos R." },
];

export default function SalesSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredOrders = MOCK_ORDERS.filter((order) => {
    const matchesSearch = order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    const matchesChannel = channelFilter === "all" || order.channel === channelFilter;
    return matchesSearch && matchesStatus && matchesChannel;
  });

  const getStatusBadge = (status: Order["status"]) => {
    const styles = {
      completed: "bg-emerald-500/20 text-emerald-500",
      pending: "bg-amber-500/20 text-amber-500",
      refunded: "bg-blue-500/20 text-blue-500",
      cancelled: "bg-red-500/20 text-red-500",
    };
    const labels = {
      completed: t("posOrders.statusCompleted"),
      pending: t("posOrders.statusPending"),
      refunded: t("posOrders.statusRefunded"),
      cancelled: t("posOrders.statusCancelled"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const todaySales = MOCK_ORDERS.filter((o) => o.date === "2024-01-15" && o.status === "completed")
    .reduce((acc, o) => acc + o.total, 0);
  const totalOrders = MOCK_ORDERS.filter((o) => o.date === "2024-01-15").length;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posOrders.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posOrders.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posOrders.export")}
          </button>
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-printer-line"></i>
            {t("posOrders.print")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.todaySales")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>${todaySales.toFixed(2)}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.todayOrders")}</span>
            <i className="ri-shopping-bag-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.avgOrderValue")}</span>
            <i className="ri-line-chart-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>$129.35</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posOrders.pendingOrders")}</span>
            <i className="ri-time-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>1</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posOrders.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posOrders.allChannels")}</option>
              <option value="pos">POS</option>
              <option value="online">Online</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posOrders.allStatuses")}</option>
              <option value="completed">{t("posOrders.statusCompleted")}</option>
              <option value="pending">{t("posOrders.statusPending")}</option>
              <option value="refunded">{t("posOrders.statusRefunded")}</option>
              <option value="cancelled">{t("posOrders.statusCancelled")}</option>
            </select>

            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-calendar-line"></i>
              {t("posOrders.dateRange")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.order")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.customer")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.items")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.total")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.payment")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.channel")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{order.orderNumber}</p>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{order.date} {order.time}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className={themeClasses.text}>{order.customer}</p>
                      {order.cashier && (
                        <p className={`text-xs ${themeClasses.textSecondary}`}>{t("posOrders.cashier")}: {order.cashier}</p>
                      )}
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-center ${themeClasses.text}`}>{order.items}</td>
                  <td className={`px-4 py-3 text-right font-bold ${themeClasses.text}`}>${order.total.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-center ${themeClasses.textSecondary}`}>{order.paymentMethod}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${order.channel === "pos" ? "bg-blue-500/20 text-blue-500" : "bg-violet-500/20 text-violet-500"}`}>
                      {order.channel.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(order.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-eye-line"></i>
                      </button>
                      <button aria-label={t("general.print", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-printer-line"></i>
                      </button>
                      {order.status === "completed" && (
                        <button className="p-2 rounded-lg hover:bg-amber-500/10 text-amber-500">
                          <i className="ri-refund-2-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
